/*
 * Watch & Buy — shoppable video carousel
 *
 * Performance notes:
 * - Each tile's inline preview (muted, looping) only starts loading once
 *   the tile scrolls near the viewport (IntersectionObserver + rootMargin),
 *   and pauses again the moment it scrolls away — tiles far down the
 *   carousel never cost network or decode time until they're about to
 *   be seen.
 * - Tapping a tile opens a separate, full <video>/<iframe> with sound in
 *   the pop-up; that element is created fresh on open and fully torn
 *   down on close.
 * - Auto-advance only runs while the carousel is actually on screen
 *   (IntersectionObserver) and is a "stop and go" step-scroll: it moves
 *   one tile, waits, moves again — never a continuous marquee.
 */
(function () {
  var REDUCED_MOTION = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var RESUME_DELAY = 4000; // ms of no interaction before auto-advance resumes
  var PREVIEW_ROOT_MARGIN = '200px 400px'; // start loading a tile's preview slightly before it's visible

  function init(section) {
    var track = section.querySelector('[data-wb-track]');
    var tiles = Array.prototype.slice.call(section.querySelectorAll('[data-wb-tile]'));
    var prevBtn = section.querySelector('[data-wb-prev]');
    var nextBtn = section.querySelector('[data-wb-next]');
    var modal = section.querySelector('[data-wb-modal]');
    if (!track || !tiles.length || !modal) return;

    // Move the pop-up to be a direct child of <body>. Some themes animate
    // sections into view with a CSS transform, and any transform on an
    // ancestor makes a `position: fixed` element fixed to THAT ancestor
    // instead of the viewport — which would shrink the dark overlay down
    // to the section's own box instead of covering the whole screen.
    // Reparenting sidesteps that entirely, wherever this section lives.
    if (modal.parentNode !== document.body) {
      document.body.appendChild(modal);
    }

    var mediaHost = modal.querySelector('[data-wb-media]');
    var productsHost = modal.querySelector('[data-wb-products-panel]');
    var closeEls = modal.querySelectorAll('[data-wb-close]');

    var autoplayEnabled = section.getAttribute('data-autoplay') === 'true' && !REDUCED_MOTION;
    var interval = parseInt(section.getAttribute('data-interval'), 10) || 3500;

    var autoTimer = null;
    var resumeTimer = null;
    var inView = false;
    var suspended = false; // true while modal is open or user is interacting
    var activeMedia = null;
    var lastFocused = null;
    var openTileEl = null;

    /* ---------------- Inline preview: autoplay muted once near view ---------------- */

    function loadEmbedPreview(embedEl) {
      var type = embedEl.getAttribute('data-embed-type');
      var id = embedEl.getAttribute('data-embed-id');
      if (!id || embedEl.getAttribute('data-loaded') === 'true') return;
      embedEl.setAttribute('data-loaded', 'true');

      var src = type === 'youtube'
        ? 'https://www.youtube.com/embed/' + id + '?autoplay=1&mute=1&loop=1&playlist=' + id + '&controls=0&playsinline=1&modestbranding=1&rel=0'
        : 'https://player.vimeo.com/video/' + id + '?autoplay=1&muted=1&loop=1&background=1&controls=0';

      var iframe = document.createElement('iframe');
      iframe.className = 'watch-buy-tile__embed-iframe';
      iframe.src = src;
      iframe.setAttribute('frameborder', '0');
      iframe.setAttribute('allow', 'autoplay; encrypted-media');
      iframe.setAttribute('tabindex', '-1');
      iframe.setAttribute('aria-hidden', 'true');
      embedEl.appendChild(iframe);
    }

    function playPreview(tile) {
      var video = tile.querySelector('.watch-buy-tile__video');
      if (video) {
        if (!video.src && video.dataset.src) {
          video.src = video.dataset.src;
          video.load();
        }
        video.play().catch(function () {});
      } else {
        var embed = tile.querySelector('.watch-buy-tile__embed');
        if (embed) loadEmbedPreview(embed);
      }
    }

    function pausePreview(tile) {
      var video = tile.querySelector('.watch-buy-tile__video');
      if (video) {
        try {
          video.pause();
        } catch (e) {}
      }
      // Embeds (YouTube/Vimeo) are left playing quietly once loaded — there's
      // no reliable cross-origin way to pause them without their JS SDKs,
      // and a small muted background loop costs little once idle.
    }

    if ('IntersectionObserver' in window) {
      var previewObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.target === openTileEl) return; // handled by the modal instead
            if (entry.isIntersecting) {
              playPreview(entry.target);
            } else {
              pausePreview(entry.target);
            }
          });
        },
        { rootMargin: PREVIEW_ROOT_MARGIN, threshold: 0.1 }
      );
      tiles.forEach(function (tile) {
        previewObserver.observe(tile);
      });
    } else {
      // No IntersectionObserver support: just start every preview once.
      tiles.forEach(playPreview);
    }

    /* ---------------- Stop-and-go auto-advance ----------------
       Index-based rather than "scroll and detect scrollend": it tracks
       which tile is current and drives a plain interval, so it can't get
       stuck if a browser doesn't fire 'scrollend' or a scroll animation
       runs long. */

    var currentIndex = 0;

    function nearestIndex() {
      var pos = track.scrollLeft;
      var closest = 0;
      var min = Infinity;
      tiles.forEach(function (tile, i) {
        var d = Math.abs(tile.offsetLeft - pos);
        if (d < min) {
          min = d;
          closest = i;
        }
      });
      return closest;
    }

    function goToIndex(i) {
      var count = tiles.length;
      if (!count) return;
      currentIndex = ((i % count) + count) % count;
      track.scrollTo({ left: tiles[currentIndex].offsetLeft, behavior: 'smooth' });
    }

    function advance() {
      goToIndex(currentIndex + 1);
    }

    function startAuto() {
      clearInterval(autoTimer);
      if (!autoplayEnabled || !inView || suspended) return;
      autoTimer = setInterval(advance, interval);
    }

    function stopAuto() {
      clearInterval(autoTimer);
      autoTimer = null;
    }

    function pauseAuto() {
      suspended = true;
      stopAuto();
      clearTimeout(resumeTimer);
    }

    function resumeAutoSoon() {
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(function () {
        suspended = false;
        currentIndex = nearestIndex();
        startAuto();
      }, RESUME_DELAY);
    }

    if (autoplayEnabled) {
      ['pointerdown', 'touchstart', 'wheel'].forEach(function (evt) {
        track.addEventListener(evt, function () {
          pauseAuto();
          resumeAutoSoon();
        }, { passive: true });
      });
      track.addEventListener('mouseenter', pauseAuto);
      track.addEventListener('mouseleave', resumeAutoSoon);

      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              inView = entry.isIntersecting;
              if (inView && !suspended) {
                currentIndex = nearestIndex();
                startAuto();
              } else {
                stopAuto();
              }
            });
          },
          { threshold: 0.2 }
        );
        io.observe(section);
      } else {
        inView = true;
        startAuto();
      }
    }

    /* ---------------- Manual arrows ---------------- */

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        pauseAuto();
        currentIndex = nearestIndex();
        goToIndex(currentIndex - 1);
        resumeAutoSoon();
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        pauseAuto();
        currentIndex = nearestIndex();
        goToIndex(currentIndex + 1);
        resumeAutoSoon();
      });
    }

    /* ---------------- Modal: open / close ---------------- */

    function readProducts(tile) {
      var node = tile.querySelector('[data-wb-products]');
      if (!node) return [];
      try {
        return JSON.parse(node.textContent) || [];
      } catch (e) {
        return [];
      }
    }

    function buildVideoEl(type, src) {
      if (type === 'youtube' || type === 'vimeo') {
        var iframe = document.createElement('iframe');
        var embed = type === 'youtube'
          ? 'https://www.youtube.com/embed/' + src + '?autoplay=1&playsinline=1&rel=0'
          : 'https://player.vimeo.com/video/' + src + '?autoplay=1';
        iframe.src = embed;
        iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
        iframe.allowFullscreen = true;
        iframe.title = 'Product video';
        return iframe;
      }

      var video = document.createElement('video');
      video.controls = true;
      video.playsInline = true;
      video.autoplay = true;
      video.muted = false;
      video.preload = 'none';
      var source = document.createElement('source');
      source.src = src;
      video.appendChild(source);
      return video;
    }

    function renderProducts(products) {
      productsHost.innerHTML = '';
      products.forEach(function (p) {
        if (!p || !p.url) return;
        var a = document.createElement('a');
        a.className = 'watch-buy-modal__product';
        a.href = p.url;

        if (p.image) {
          var img = document.createElement('img');
          img.src = p.image;
          img.alt = '';
          img.loading = 'lazy';
          a.appendChild(img);
        }

        var text = document.createElement('span');
        text.className = 'watch-buy-modal__product-text';

        var title = document.createElement('span');
        title.className = 'watch-buy-modal__product-title';
        title.textContent = p.title || '';
        text.appendChild(title);

        if (p.price) {
          var price = document.createElement('span');
          price.className = 'watch-buy-modal__product-price';
          price.textContent = p.price;
          text.appendChild(price);
        }

        a.appendChild(text);
        productsHost.appendChild(a);
      });
    }

    function openTile(tile) {
      var type = tile.getAttribute('data-video-type');
      var src = type === 'youtube' || type === 'vimeo'
        ? tile.getAttribute('data-video-id')
        : tile.getAttribute('data-video-src');
      if (!src || type === 'none') return;

      pauseAuto();
      pausePreview(tile);
      openTileEl = tile;
      lastFocused = document.activeElement;

      mediaHost.innerHTML = '';
      activeMedia = buildVideoEl(type, src);
      mediaHost.appendChild(activeMedia);

      renderProducts(readProducts(tile));

      modal.setAttribute('data-open', '');
      modal.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', onKeydown);

      var closeBtn = modal.querySelector('.watch-buy-modal__close');
      if (closeBtn) closeBtn.focus();
    }

    function closeModal() {
      if (activeMedia && activeMedia.tagName === 'VIDEO') {
        try {
          activeMedia.pause();
        } catch (e) {}
      }
      modal.removeAttribute('data-open');
      modal.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKeydown);

      // Fully tear the media element down so nothing keeps downloading/decoding.
      mediaHost.innerHTML = '';
      activeMedia = null;
      productsHost.innerHTML = '';

      if (openTileEl) {
        playPreview(openTileEl);
        openTileEl = null;
      }

      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus();
      }
      resumeAutoSoon();
    }

    function onKeydown(e) {
      if (e.key === 'Escape') closeModal();
    }

    tiles.forEach(function (tile) {
      var openBtn = tile.querySelector('[data-wb-open]');
      if (openBtn) {
        openBtn.addEventListener('click', function () {
          openTile(tile);
        });
      }
    });

    closeEls.forEach(function (el) {
      el.addEventListener('click', closeModal);
    });
  }

  function boot() {
    document.querySelectorAll('[data-watch-buy]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector('[data-watch-buy]');
    if (section) init(section);
  });
})();
